import React, { ChangeEvent, useContext, useEffect, useState, useMemo } from "react";
import { GlobalContext } from "context/Provider";
import Session from "helpers/session";
import API from "api";
import { useNavigate } from "react-router";
import { Grid } from "@mui/material";
import SimpleGrid, {
  CellProps,
  BodyProps
} from "components/shared/SimpleGrid/SimpleGrid";
import { StyledSimpleGridContainer } from "../List/List.styles";
import { EncounterTimerDisplay } from "components/shared/EncounterTimer/EncounterTimer";
import { useTranslation } from "react-i18next";
import { associateReadingType } from "helpers/variables";
import {
  uBreathing,
  uCoordinator,
  uDate,
  uDiastolic,
  uGlucose,
  uMeal,
  uNote,
  uOxygenSaturation,
  uPulse,
  uSystolic,
  uTemperature,
  uWeight,
  uActivity,
  uSleep
} from "helpers/translationStringConstants";
import { COORDINATOR, UNITS } from "helpers/appConstants";
import { READING_TYPES } from "helpers/appConstants";
import { formatTableBody } from "./List.util";
import {
  HiddenFieldsChips,
  useSpirometryVisibility,
  spirometryLabels,
  defaultSpirometryVisibility
} from "views/PatientBoard/microViews/History/Measurements/Measurements.utils";

const { heartRate } = READING_TYPES;

interface ListProps {
  type: string;
}

interface HeadersInterface {
  "Blood Pressure": CellProps[];
  "Blood Glucose": CellProps[];
  "Heart Rate": CellProps[];
  "Oxygen Saturation": CellProps[];
  Spirometry: CellProps[];
  Temperature: CellProps[];
  Weight: CellProps[];
  Activity: CellProps[];
  Sleep: CellProps[];
  "Respiration Rate": CellProps[];
  "Member Note": CellProps[];
}

export default function List({type}: ListProps) {
  const { t } = useTranslation();
  const memberId = window.location.pathname.split("/").pop();
  const [readingList, setReadingList] = useState<BodyProps[]>([]);
  const { config, readingsDispatch, settings, settingsDispatch } = useContext(GlobalContext);
  const navigate = useNavigate();
  const [totalReadingList, setTotalReadingList] = useState<number>(0);
  const heartRateToggle = type !== heartRate.display;
  const { visibility, hiddenFields, updateVisibility } = useSpirometryVisibility();

  const paginationAction = (itemsPerPage?: number, currentPage?: number) => {
    const additionalSources = heartRateToggle ? "" : "&type=spo2&type=bp";

    API.kore.readings
      .getForOne(
        memberId || "",
        Session.crackCookieToken("coordinator"),
        associateReadingType(type),
        `${additionalSources}&limit=${itemsPerPage}&page=${currentPage}&order=desc&sort_by=measured_at`
      )
      .then((readings: any) => {
        setReadingList(
          formatTableBody(
            readings.items,
            selectReading,
            config?.basicOptions.units,
            type
          ).map((row: any) => ({
            ...row,
            id: row.id,
            action: () => selectReadingById(row.id)
          }))
        );
      });
  };

  const headers: HeadersInterface = {
    "Blood Pressure": [
      { content: t(uDate), align: "center" },
      {
        content: `${t(uSystolic)}, ${UNITS.bloodPressure.mmHg}`,
        align: "center"
      },
      {
        content: `${t(uDiastolic)}, ${UNITS.bloodPressure.mmHg}`,
        align: "center"
      },
      { content: `${t(uPulse)}, ${UNITS.heartRate.bpm}`, align: "center" }
    ],
    "Blood Glucose": [
      { content: t(uDate), align: "center" },
      { content: `${t(uGlucose)}, ${UNITS.glucose.mg}`, align: "center" },
      { content: t(uMeal), align: "center" }
    ],
    "Heart Rate": [
      { content: t(uDate), align: "center" },
      { content: `${t(uPulse)}, ${UNITS.heartRate.bpm}`, align: "center" },
      { content: "Source Device", align: "center" }
    ],
    "Oxygen Saturation": [
      { content: t(uDate), align: "center" },
      { content: t(uOxygenSaturation), align: "center" }
    ],
    Spirometry: [
      { content: t(uDate), align: "center" },
      visibility.fvc && { content: "FVC,L", align: "center" },
      visibility.fev1 && { content: "FEV1,L", align: "center" },
      visibility.fev1fvc && { content: "FEV1/FVC", align: "center" },
      visibility.pef && { content: "PEF", align: "center" }
    ].filter(Boolean) as { content: string; align: "center" }[],
    Temperature: [
      { content: t(uDate), align: "center" },
      { content: `${t(uTemperature)}, F`, align: "center" }
    ],
    Weight: [
      { content: t(uDate), align: "center" },
      { content: `${t(uWeight)}, ${UNITS.weight.pounds}`, align: "center" }
    ],
    Activity: [
      { content: t(uDate), align: "center" },
      { content: `${t(uActivity)}, ${UNITS.activity.steps}`, align: "center" }
    ],
    Sleep: [
      { content: t(uDate), align: "center" },
      { content: `${t(uSleep)}, ${UNITS.sleep.hours}`, align: "center" }
    ],
    "Respiration Rate": [
      { content: t(uDate), align: "center" },
      { content: t(uBreathing), align: "center" }
    ],
    "Member Note": [
      { content: t(uDate), align: "center" },
      { content: t(uCoordinator), align: "center" },
      { content: t(uNote), align: "center" }
    ]
  };
  //This doesn't work with the structure of simple grid and only causes the Attributes page to render with
  //the full list of readings, but simplegrid still requires this event to be passed to it
  const selectReading = (event: any) => {
    //TODO: utilize Mel call to avoid improper tier access on readings
    API.kore.readings
      .getSingleReading(
        event.target.dataset.id || "",
        Session.crackCookieToken(COORDINATOR)
      )
      .then((reading) => {
        //TODO: ensure all necessary data is present for reading details
        readingsDispatch({ type: "UPDATE_READING", payload: reading });
        const currentPath = window.location.pathname;
        navigate(currentPath.replace("list", "details"));
      });
  };
  //This is used for the action upon clicking the row
  const selectReadingById = (readingId: string) => {
    if (!readingId) return;

    API.kore.readings
      .getSingleReading(readingId, Session.crackCookieToken(COORDINATOR))
      .then((reading) => {
        readingsDispatch({ type: "UPDATE_READING", payload: reading });
        const currentPath = window.location.pathname;
        navigate(currentPath.replace("list", `${readingId}`));
      });
  };

  const filteredReadingList = useMemo(() => {
    if (type !== "Spirometry") return readingList;

    return readingList.map((row) => {
      const indexes: number[] = [0];
      if (visibility.fvc) indexes.push(1);
      if (visibility.fev1) indexes.push(2);
      if (visibility.fev1fvc) indexes.push(3);
      if (visibility.pef) indexes.push(4);

      return {
        ...row,
        cells: indexes
          .map((i) => row.cells[i])
          .filter((cell) => cell !== undefined),
      };
    });
  }, [readingList, visibility, type]);

  useEffect(() => {
    switch (type) {
      case "Member Note":
        //TODO: API call for Notes
        setReadingList([]);
        break;
      case "ECG":
        //TODO: API call for ECG if necessary
        setReadingList([]);
        break;
      default:
        API.kore.readings
          .getCount(
            memberId || "",
            Session.crackCookieToken(COORDINATOR),
            associateReadingType(type)
          )
          .then((readings) => {
            setTotalReadingList(readings.count);
            paginationAction(10, 0);
          });
    }
  }, [type]);

  return (
    <Grid container>
      <Grid item xs={0.5} lg={0.7} />
      <Grid item xs={11.2} lg={11.1}>
        <StyledSimpleGridContainer>
          <EncounterTimerDisplay/>
          {type === "Spirometry" && (
            <HiddenFieldsChips
              hiddenFields={hiddenFields}
              labels={spirometryLabels}
              sx={{ pl: 2 }}
              onRestore={(field) =>
                updateVisibility({
                  ...visibility,
                  [field]: true,
                })
              }
            />
          )}
          <SimpleGrid
            headers={headers[type as keyof HeadersInterface]}
            body={filteredReadingList}
            paginationAction={paginationAction}
            totalList={totalReadingList}
          />
        </StyledSimpleGridContainer>
      </Grid>
    </Grid>
  );
}
